import { useEffect, useState } from 'react';

declare const process: { env: { ADMIN_API_BASE?: string } };

const API_BASE = process.env.ADMIN_API_BASE || '';
const AUTH_REFRESH_URL = 'https://auth.bepro.ai/api/v1/auth/refresh';
const SIGN_IN_URL = 'https://pages.bepro.ai/auth/v2/sign-in';

let accessToken = '';

const getCookie = (name: string) => document.cookie.split('; ').find((c) => c.startsWith(`${name}=`))?.split('=')[1] || '';

export const refresh = async () => {
    const csrf = getCookie('beproAuthCsrfToken');
    try {
        const response = await fetch(AUTH_REFRESH_URL, {
            method: 'POST',
            credentials: 'include',
            headers: csrf ? { 'X-CSRFToken': decodeURIComponent(csrf) } : {},
            signal: AbortSignal.timeout(10000),
        });
        accessToken = response.ok ? (await response.json()).accessToken : '';
    } catch (err) {
        console.error('Auth refresh failed', err);
        accessToken = '';
    }
    return !!accessToken;
};

export const redirectToSignIn = () => {
    location.href = `${SIGN_IN_URL}?returnUrl=${encodeURIComponent(location.href)}`;
};

const send = (url: string, options: RequestInit) =>
    fetch(`${API_BASE}${url}`, { ...options, headers: { 'Authorization': `Bearer ${accessToken}`, ...options.headers } });

export const apiFetch = async (url: string, options: RequestInit = {}) => {
    let response = await send(url, options);
    if (response.status === 401) {
        if (await refresh()) {
            response = await send(url, options);
        }
        if (response.status === 401) redirectToSignIn();
    }
    return response;
};

export const fetchObjectUrl = async (url: string) => {
    const response = await apiFetch(url);
    return response.ok ? URL.createObjectURL(await response.blob()) : '';
};

export const fetchSignedImageUrl = async (gcsPath: string) => {
    const response = await apiFetch(`/train/image_url?gcs_path=${encodeURIComponent(gcsPath)}`);
    return response.ok ? ((await response.json()).url as string) : '';
};

export const useSignedImageUrl = (gcsPath: string | null) => {
    const [url, setUrl] = useState('');
    useEffect(() => {
        setUrl('');
        if (!gcsPath) return;
        let cancelled = false;
        fetchSignedImageUrl(gcsPath).then((u) => {
            if (!cancelled) setUrl(u);
        });
        return () => {
            cancelled = true;
        };
    }, [gcsPath]);
    return url;
};

export const useObjectUrl = (url: string | null) => {
    const [objectUrl, setObjectUrl] = useState('');
    useEffect(() => {
        setObjectUrl('');
        if (!url) return;
        let created = '';
        let cancelled = false;
        fetchObjectUrl(url).then((u) => {
            if (cancelled) return URL.revokeObjectURL(u);
            created = u;
            setObjectUrl(u);
        });
        return () => {
            cancelled = true;
            if (created) URL.revokeObjectURL(created);
        };
    }, [url]);
    return objectUrl;
};
